import { useTranslation } from 'react-i18next';
import { Monitor, Smartphone, LogOut } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { ColumnDef } from '@tanstack/react-table';
import { Session } from '@/api-client/use-session/types';
import { CustomtDateFormat } from '@/lib/utils/custom-date/custom-date';
import { useSessionRevoke } from '@/api-client/use-session-revoke';
import { useQueryClient } from '@tanstack/react-query';
/**
 * Custom hook that defines and returns the columns for the device session table.
 * It defines column properties such as headers, cell rendering logic, and formatting.
 *
 */

export const useDeviceTableColumns = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: revokeSession, isPending } = useSessionRevoke();

  const getDeviceIcon = (os: string | undefined) => {
    if (!os) return <Monitor className="w-5 h-5 text-secondary" />;

    const osLower = os.toLowerCase();
    if (
      osLower.includes('ios') ||
      osLower.includes('android') ||
      osLower.includes('mobile') ||
      osLower.includes('phone')
    ) {
      return <Smartphone className="w-5 h-5 text-secondary" />;
    }
    return <Monitor className="w-5 h-5 text-secondary" />;
  };

  const columns: ColumnDef<Session>[] = [
    {
      id: 'device',
      header: () => <span className="flex min-w-40 sm:min-w-50 items-center">{t('DEVICE')}</span>,
      cell: ({ row }) => (
        <div className="flex min-w-40 sm:min-w-50 items-center">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/10 shrink-0">
            {getDeviceIcon(row.original.primaryOperatingSystem)}
          </div>
          <span className="ml-3 truncate font-semibold">
            {(row.original.primaryDeviceName || row.original.primaryOperatingSystem) || t('UNKNOWN_DEVICE')}
          </span>
        </div>
      ),
    },
    {
      id: 'browser',
      header: () => (
        <span className="flex min-w-30 sm:min-w-37.5 items-center">{t('BROWSER')}</span>
      ),
      cell: ({ row }) => <span className="truncate block min-w-30 sm:min-w-37.5">{row.original.primaryBrowser || t('UNKNOWN_BROWSER')}</span>,
    },
    {
      id: 'lastAccessed',
      header: () => (
        <span className="flex min-w-37.5 sm:min-w-45 items-center">{t('LAST_ACCESSED')}</span>
      ),
      cell: ({ row }) => <span className="whitespace-nowrap block min-w-37.5 sm:min-w-45 text-muted-foreground">{CustomtDateFormat(row.original.lastActivityAt, { showTime: true })}</span>,
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={isPending}
            onClick={() => {
              revokeSession(
                { sessionId: row.original.sessionId },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ['session'] });
                  },
                }
              );
            }}
            variant="ghost"
            className="group text-destructive hover:bg-destructive/10 hover:text-destructive disabled:opacity-50 transition-colors"
          >
            <LogOut className="h-4 w-4 mr-2 opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" />
            <span className="text-sm font-semibold">{t('LOG_OUT')}</span>
          </Button>
        </div>
      ),
    },
  ];

  return columns;
};
